import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

type SidebarDrillTransitionProps = {
  /** When set, shows drillMenu with a slide-in transition; null shows rootMenu. */
  drillKey: string | null;
  rootMenu: React.ReactNode;
  drillMenu: React.ReactNode;
};

const SLIDE_EASE = [0.32, 0.72, 0, 1] as const;
const SLIDE_DURATION = 0.3;

/**
 * Two-panel horizontal track: one transform animates root ↔ drill (no mount/unmount blink).
 * Invisible spacer sets height from the visible panel only.
 */
const SidebarDrillTransition: React.FC<SidebarDrillTransitionProps> = ({
  drillKey,
  rootMenu,
  drillMenu,
}) => {
  const reduceMotion = useReducedMotion();
  const isDrilled = drillKey != null;

  if (reduceMotion) {
    return <div>{isDrilled ? drillMenu : rootMenu}</div>;
  }

  return (
    <div className="relative overflow-hidden">
      <div className="invisible pointer-events-none" aria-hidden="true">
        {isDrilled ? drillMenu : rootMenu}
      </div>

      <motion.div
        className="absolute inset-0 flex w-[200%] will-change-transform"
        initial={false}
        animate={{ x: isDrilled ? '-50%' : '0%' }}
        transition={{ duration: SLIDE_DURATION, ease: SLIDE_EASE }}
      >
        <div className="w-1/2 min-w-0 shrink-0">{rootMenu}</div>
        <div className="w-1/2 min-w-0 shrink-0 overflow-x-hidden">
          <AnimatePresence mode="popLayout" initial={false}>
            {isDrilled ? (
              <motion.div
                key={drillKey}
                initial={{ opacity: 0.85 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0.85 }}
                transition={{ duration: 0.12 }}
              >
                {drillMenu}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default SidebarDrillTransition;
